import { useEffect, useMemo, useState } from "react";
import { Plus, FlagBanner, X, FunnelSimple } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";

const COLS = [
  { key: "TODO", label: "To do", accent: "text-neon-pink", chip: "border-neon-pink/30 bg-neon-pink/10 text-neon-pink" },
  { key: "DOING", label: "Doing", accent: "text-neon-amber", chip: "border-neon-amber/30 bg-neon-amber/10 text-neon-amber" },
  { key: "DONE", label: "Done", accent: "text-neon-lime", chip: "border-neon-lime/30 bg-neon-lime/10 text-neon-lime" },
];
const PRIORITY_TINT = { HIGH: "text-neon-pink", MEDIUM: "text-neon-amber", LOW: "text-neon-lime" };

export default function Tasks() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [fStatus, setFStatus] = useState("ALL");
  const [fPriority, setFPriority] = useState("ALL");

  async function load() {
    const { data } = await api.get("/api/tasks");
    setItems(data.items);
  }
  useEffect(() => { load(); }, []);

  async function setStatus(t, status) {
    await api.patch(`/api/tasks/${t.id}/status`, { status });
    load();
  }

  const filtered = useMemo(() => items.filter((t) =>
    (fStatus === "ALL" || t.status === fStatus) &&
    (fPriority === "ALL" || t.priority === fPriority)
  ), [items, fStatus, fPriority]);

  const counts = useMemo(() => ({
    ALL: items.length,
    TODO: items.filter((x) => x.status === "TODO").length,
    DOING: items.filter((x) => x.status === "DOING").length,
    DONE: items.filter((x) => x.status === "DONE").length,
  }), [items]);

  return (
    <div className="space-y-4 font-sans" data-testid="tasks-screen">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">Work queue</p>
          <h1 className="font-display text-3xl font-extrabold text-white">Tasks</h1>
        </div>
        <button data-testid="add-task-fab" onClick={() => setOpen(true)}
          className="flex items-center gap-1.5 rounded-full bg-neon-cyan px-4 py-2.5 text-sm font-bold text-black shadow-glow-cyan active:scale-95">
          <Plus size={16} weight="bold" /> New
        </button>
      </div>

      {/* Filters */}
      <div className="card flex items-center gap-2 p-2" data-testid="task-filters">
        <FunnelSimple size={16} weight="bold" className="ml-2 text-white/50" />
        <select className="field !py-2 text-[13px]" value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="filter-status">
          <option value="ALL" className="bg-ink-800">All status ({counts.ALL})</option>
          <option value="TODO" className="bg-ink-800">To do ({counts.TODO})</option>
          <option value="DOING" className="bg-ink-800">Doing ({counts.DOING})</option>
          <option value="DONE" className="bg-ink-800">Done ({counts.DONE})</option>
        </select>
        <select className="field !py-2 text-[13px]" value={fPriority} onChange={(e) => setFPriority(e.target.value)} data-testid="filter-priority">
          <option value="ALL" className="bg-ink-800">All priority</option>
          <option value="HIGH" className="bg-ink-800">High</option>
          <option value="MEDIUM" className="bg-ink-800">Medium</option>
          <option value="LOW" className="bg-ink-800">Low</option>
        </select>
      </div>

      <div className="space-y-5">
        {COLS.map((c) => {
          if (fStatus !== "ALL" && fStatus !== c.key) return null;
          const list = filtered.filter((i) => i.status === c.key);
          return (
            <section key={c.key}>
              <div className="mb-2 flex items-center justify-between">
                <p className={`text-[11px] font-bold uppercase tracking-[0.22em] ${c.accent}`}>{c.label}</p>
                <span className="chip">{list.length}</span>
              </div>
              {list.length === 0 ? (
                <div className="card p-4 text-[13px] text-white/45">Nothing here.</div>
              ) : (
                <div className="space-y-2">
                  {list.map((t) => (
                    <div key={t.id} className="card p-4" data-testid={`task-${t.id}`}>
                      <div className="flex items-start gap-2">
                        <FlagBanner size={16} weight="fill" className={`mt-1 shrink-0 ${PRIORITY_TINT[t.priority] || "text-white/50"}`} />
                        <div className="min-w-0 flex-1">
                          <p className="font-display text-[15px] font-semibold text-white">{t.title}</p>
                          {t.description && <p className="mt-0.5 text-[12px] text-white/55">{t.description}</p>}
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            <span className={`chip ${c.chip}`}>{c.label}</span>
                            <span className="chip">{t.priority}</span>
                            {t.dueDate && <span className="chip">Due {new Date(t.dueDate).toLocaleDateString()}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {COLS.filter((x) => x.key !== c.key).map((x) => (
                          <button key={x.key} onClick={() => setStatus(t, x.key)} data-testid={`task-move-${t.id}-${x.key}`}
                            className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white/70 hover:bg-white/10">
                            → {x.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      <AnimatePresence>{open && <NewTaskSheet onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}</AnimatePresence>
    </div>
  );
}

function NewTaskSheet({ onClose, onCreated }) {
  const [title, setTitle] = useState(""); const [desc, setDesc] = useState("");
  const [priority, setPriority] = useState("MEDIUM"); const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/api/tasks", { title: title.trim(), description: desc.trim(), priority, dueDate: due || null });
      onCreated();
    } finally { setBusy(false); }
  }

  return (
    <motion.div className="fixed inset-0 z-50 flex items-end justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.form onSubmit={submit} initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="relative w-full max-w-md rounded-t-[2rem] border border-white/10 bg-ink-900 p-6 pb-safe">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15" />
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-white">New task</h3>
          <button type="button" onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button>
        </div>
        <div className="mt-4 space-y-3">
          <div><label className="label">Title</label>
            <input className="field" value={title} onChange={(e) => setTitle(e.target.value)} required minLength={2} data-testid="task-title" /></div>
          <div><label className="label">Description</label>
            <textarea rows={3} className="field" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="task-desc" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="label">Priority</label>
              <select className="field !py-3" value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="task-priority">
                <option className="bg-ink-800" value="LOW">Low</option>
                <option className="bg-ink-800" value="MEDIUM">Medium</option>
                <option className="bg-ink-800" value="HIGH">High</option>
              </select></div>
            <div><label className="label">Due</label>
              <input type="date" className="field !py-3" value={due} onChange={(e) => setDue(e.target.value)} data-testid="task-due" /></div>
          </div>
          <button className="btn-primary w-full" disabled={busy} data-testid="task-submit">{busy ? "Saving…" : "Create task"}</button>
        </div>
      </motion.form>
    </motion.div>
  );
}
